/**
 * addonsConfig.js — Catalogue add-ons côté frontend (v5 Mytenue)
 *
 * Doit rester strictement aligné avec aws/lambda/addons-config.js (backend).
 *
 * v1.12.580 — Phase V5.1 bundle v5.
 *
 * Wardrobe model HYBRIDE v5 :
 *   - addon_wardrobe_boost_50 : consumable TTL 45j, tous tiers
 *   - subscription_wardrobe_plus : sub mensuelle, Basic+ uniquement
 *   - subscription_wardrobe_max : sub mensuelle, Basic+ uniquement
 *
 * Note : scans_50 reste à 1,49 € (dérogation v5 spec 0,99 €, validée par user).
 *
 * v1.12.609 — Quantités scans révisées : 50→25 et 200→100 (prix inchangés).
 * Les IDs et revenueCatIdentifier restent stables pour préserver les achats
 * existants. Doit rester aligné avec aws/lambda/addons-config.js.
 */

export const ADDONS = {
  // ─── Try-Ons (consumable, TTL 30j, tous tiers) ───
  addon_tryons_3:   { id: 'addon_tryons_3',   type: 'tryons',   consumability: 'consumable', quantity: 3,   priceEur: 1.99, ttlDays: 30,  recurringInterval: null, eligibleTiers: ['free','basic','premium','gold'], badge: null,
    labelFr: 'Pack Découverte', labelEn: 'Starter Pack',
    descFr: '3 Try-Ons supplémentaires pendant 30 jours.', descEn: '3 extra Try-Ons for 30 days.' },
  addon_tryons_10:  { id: 'addon_tryons_10',  type: 'tryons',   consumability: 'consumable', quantity: 10,  priceEur: 4.99, ttlDays: 30,  recurringInterval: null, eligibleTiers: ['free','basic','premium','gold'], badge: 'popular',
    labelFr: 'Pack Régulier', labelEn: 'Regular Pack',
    descFr: '10 Try-Ons supplémentaires pendant 30 jours.', descEn: '10 extra Try-Ons for 30 days.' },
  addon_tryons_25:  { id: 'addon_tryons_25',  type: 'tryons',   consumability: 'consumable', quantity: 25,  priceEur: 9.99, ttlDays: 30,  recurringInterval: null, eligibleTiers: ['free','basic','premium','gold'], badge: 'best_value',
    labelFr: 'Pack Power', labelEn: 'Power Pack',
    descFr: '25 Try-Ons supplémentaires pendant 30 jours.', descEn: '25 extra Try-Ons for 30 days.' },

  // ─── AI Scans (consumable, TTL 24h/7j, tous tiers) ───
  addon_scans_50:   { id: 'addon_scans_50',   type: 'scans',    consumability: 'consumable', quantity: 25,  priceEur: 1.49, ttlDays: 1,   recurringInterval: null, eligibleTiers: ['free','basic','premium','gold'], badge: null,
    labelFr: 'Boost Shopping', labelEn: 'Shopping Boost',
    descFr: '+25 analyses IA pendant 24h.', descEn: '+25 AI scans for 24h.' },
  addon_scans_200:  { id: 'addon_scans_200',  type: 'scans',    consumability: 'consumable', quantity: 100, priceEur: 3.99, ttlDays: 7,   recurringInterval: null, eligibleTiers: ['free','basic','premium','gold'], badge: 'popular',
    labelFr: 'Boost Saison', labelEn: 'Season Boost',
    descFr: '+100 analyses IA pendant 7 jours.', descEn: '+100 AI scans for 7 days.' },

  // ─── Wardrobe MODÈLE HYBRIDE v5 ───
  addon_wardrobe_boost_50: { id: 'addon_wardrobe_boost_50', type: 'wardrobe_boost', consumability: 'consumable',
    quantity: 50, priceEur: 1.99, ttlDays: 45, recurringInterval: null,
    eligibleTiers: ['free','basic','premium','gold'], badge: null,
    labelFr: 'Boost +50', labelEn: 'Boost +50',
    descFr: '+50 slots dans votre dressing pendant 45 jours. Achat unique non récurrent.',
    descEn: '+50 wardrobe slots for 45 days. One-time purchase, non-recurring.' },
  subscription_wardrobe_plus: { id: 'subscription_wardrobe_plus', type: 'wardrobe_subscription', consumability: 'subscription',
    quantity: 100, priceEur: 0.99, ttlDays: null, recurringInterval: 'month',
    eligibleTiers: ['basic','premium','gold'], badge: 'popular',
    labelFr: 'Espace Plus', labelEn: 'Space Plus',
    descFr: '+100 slots permanents. Abonnement mensuel résiliable à tout moment. Nécessite Basic ou supérieur.',
    descEn: '+100 permanent slots. Monthly subscription, cancellable anytime. Requires Basic or higher.' },
  subscription_wardrobe_max:  { id: 'subscription_wardrobe_max',  type: 'wardrobe_subscription', consumability: 'subscription',
    quantity: 250, priceEur: 2.49, ttlDays: null, recurringInterval: 'month',
    eligibleTiers: ['basic','premium','gold'], badge: 'best_value',
    labelFr: 'Espace Max', labelEn: 'Space Max',
    descFr: '+250 slots permanents. Abonnement mensuel résiliable à tout moment. Nécessite Basic ou supérieur.',
    descEn: '+250 permanent slots. Monthly subscription, cancellable anytime. Requires Basic or higher.' },
};

// Mapping feature paywall → type d'addon à proposer
const FEATURE_TO_ADDON_TYPE = {
  ai: 'scans',
  looks: 'scans',
  tryon: 'tryons',
  wardrobe: 'wardrobe_boost',
};

// Retourne les addons à proposer dans le paywall selon feature et tier.
// v1.12.587 — pour wardrobe : on retourne TOUS les addons wardrobe (Boost + Plus + Max)
// même si non éligibles au tier. Les non-éligibles sont marqués `_locked: true`
// pour que l'UI les affiche grisés avec un badge "Basic+ requis".
// v1.12.583 : 'wardrobe_and_ai' retourne tous wardrobe (avec lock) + scans éligibles.
export function getAddonsForFeature(feature, tier = 'gold') {
  // Cas combiné wardrobe + ai : merge des 2 listes
  if (feature === 'wardrobe_and_ai') {
    const wardrobeAddons = Object.values(ADDONS)
      .filter(a => a.type === 'wardrobe_boost' || a.type === 'wardrobe_subscription')
      .map(a => ({ ...a, _locked: !a.eligibleTiers.includes(tier) }));
    const scansAddons = Object.values(ADDONS).filter(a =>
      a.type === 'scans' && a.eligibleTiers.includes(tier)
    );
    return [...wardrobeAddons, ...scansAddons].sort((a, b) => a.quantity - b.quantity);
  }
  // Cas wardrobe seul : tous les wardrobe addons avec flag _locked
  if (feature === 'wardrobe') {
    return Object.values(ADDONS)
      .filter(a => a.type === 'wardrobe_boost' || a.type === 'wardrobe_subscription')
      .map(a => ({ ...a, _locked: !a.eligibleTiers.includes(tier) }))
      .sort((a, b) => a.quantity - b.quantity);
  }
  const type = FEATURE_TO_ADDON_TYPE[feature];
  if (!type) return [];
  const items = Object.values(ADDONS).filter(a =>
    a.type === type && a.eligibleTiers.includes(tier)
  );
  return items.sort((a, b) => a.quantity - b.quantity);
}

// Formate une ligne addon : "+25 AI scans · 1,49 €" ou "+100 slots · 0,99 €/mois"
export function formatAddonLine(addon, lang) {
  const isFR = !lang || lang.startsWith('fr');
  const price = addon.priceEur.toFixed(2).replace('.', isFR ? ',' : '.');
  const recurringSuffix = addon.recurringInterval === 'month' ? (isFR ? '/mois' : '/mo') : '';
  const unit = addon.type === 'tryons'                ? 'Try-Ons'
            : addon.type === 'scans'                  ? (isFR ? 'AI scans' : 'AI scans')
            : addon.type === 'wardrobe_boost'         ? (isFR ? 'slots (45j)' : 'slots (45d)')
            : addon.type === 'wardrobe_subscription'  ? (isFR ? 'slots permanents' : 'permanent slots')
            : 'slots';
  return `+${addon.quantity} ${unit} · ${price} €${recurringSuffix}`;
}

export function isAddonEligibleForTier(addonId, tier) {
  return ADDONS[addonId]?.eligibleTiers.includes(tier) ?? false;
}

// Helper utilisé par l'écran Boutique pour le tab Wardrobe
export function getWardrobeAddons(tier) {
  const boost = ADDONS.addon_wardrobe_boost_50;
  const plus  = ADDONS.subscription_wardrobe_plus;
  const max   = ADDONS.subscription_wardrobe_max;
  return {
    boost,
    plus,
    max,
    canSubscribePlus: plus.eligibleTiers.includes(tier),
    canSubscribeMax:  max.eligibleTiers.includes(tier),
  };
}

export function isPaidTier(tier) {
  return tier !== 'free' && !!tier;
}
